/**
 * 簡易予測 (WMA 系) 設定ダイアログの取込元日付の表示 (日付種別バッジ・行の警告) を
 * backend の応答から引く区分写像。判定そのもの (日付種別・候補にならない理由・実績の
 * 有無) は backend が返し、ここでは表示の形へ写すだけ。
 */
import {
  DayType,
  type SimpleForecastEntryDefault,
  SimpleForecastSourceDateMismatch,
  WeekdayCategory,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

/** 日付種別 (4 区分) の表示文言 key。単日画面の対象日と同じ文言を使う。 */
export const SOURCE_DATE_DAY_TYPE_LABEL_KEY: Record<
  Exclude<DayType, DayType.UNSPECIFIED>,
  string
> = {
  [DayType.WEEKDAY]: 'page:demandForecastDaily.header.dayType.weekday',
  [DayType.SATURDAY]: 'page:demandForecastDaily.header.dayType.saturday',
  [DayType.HOLIDAY]: 'page:demandForecastDaily.header.dayType.holiday',
  [DayType.SINGULAR]: 'page:demandForecastDaily.header.dayType.singular',
}

const WEEKDAY_CATEGORY_TO_DAY_TYPE: Record<
  WeekdayCategory,
  Exclude<DayType, DayType.UNSPECIFIED>
> = {
  [WeekdayCategory.UNSPECIFIED]: DayType.WEEKDAY,
  [WeekdayCategory.WEEKDAY]: DayType.WEEKDAY,
  [WeekdayCategory.SATURDAY]: DayType.SATURDAY,
  [WeekdayCategory.HOLIDAY]: DayType.HOLIDAY,
}

/**
 * 行に出す日付種別。`day_type` を返さない旧 backend (UNSPECIFIED) では
 * `weekday_category` の 3 区分で出す。
 */
export function sourceDateDisplayDayType(
  entry: Pick<SimpleForecastEntryDefault, 'dayType' | 'weekdayCategory'>,
): Exclude<DayType, DayType.UNSPECIFIED> {
  return entry.dayType === DayType.UNSPECIFIED
    ? WEEKDAY_CATEGORY_TO_DAY_TYPE[entry.weekdayCategory]
    : entry.dayType
}

/** 日区分 (3 区分) の表示文言 key。UNSPECIFIED は表示できない。 */
export function weekdayCategoryLabelKey(
  category: WeekdayCategory,
): string | undefined {
  return category === WeekdayCategory.UNSPECIFIED
    ? undefined
    : SOURCE_DATE_DAY_TYPE_LABEL_KEY[WEEKDAY_CATEGORY_TO_DAY_TYPE[category]]
}

/** 行の直下に出す警告。 */
export type SourceDateRowWarning =
  | { kind: 'noActual' }
  | { kind: 'notSameSingularGroup'; targetSingularGroup: number }
  | { kind: 'singularGroupDay' }
  | {
      kind: 'dayCategory'
      sourceCategoryLabelKey: string
      targetCategoryLabelKey: string
    }

type RowInput = Pick<
  SimpleForecastEntryDefault,
  'actualsMissing' | 'sourceDateMismatch' | 'weekdayCategory'
> & {
  /** ユーザーが日付を手で変えた行か。 */
  manual: boolean
}

/**
 * 行の警告を決める。
 *
 * - 実績の無い日 (AC-DFM-017) は手で変えたかに依らず出す。AC-DFM-016 の対象外
 *   (spec の ※) なので理由別の警告より先に返す
 * - AC-DFM-016 は手で変えた行だけ。起動時の行は選定ロジックどおりなので出さない
 * - NONE と UNSPECIFIED (旧 backend = 判定なし) は出さない
 * - 文言に要る値 (対象日の特異グループ・日区分) が応答に無い時は、穴の空いた文を出さない
 */
export function sourceDateRowWarning(
  row: RowInput,
  target: {
    weekdayCategory: WeekdayCategory
    singularGroup: number | undefined
  },
): SourceDateRowWarning | undefined {
  if (row.actualsMissing) return { kind: 'noActual' }
  if (!row.manual) return undefined
  switch (row.sourceDateMismatch) {
    case SimpleForecastSourceDateMismatch.NOT_SAME_SINGULAR_GROUP:
      return target.singularGroup === undefined
        ? undefined
        : {
            kind: 'notSameSingularGroup',
            targetSingularGroup: target.singularGroup,
          }
    case SimpleForecastSourceDateMismatch.SINGULAR_GROUP_DAY:
      return { kind: 'singularGroupDay' }
    case SimpleForecastSourceDateMismatch.DAY_CATEGORY: {
      const sourceCategoryLabelKey = weekdayCategoryLabelKey(
        row.weekdayCategory,
      )
      const targetCategoryLabelKey = weekdayCategoryLabelKey(
        target.weekdayCategory,
      )
      return sourceCategoryLabelKey === undefined ||
        targetCategoryLabelKey === undefined
        ? undefined
        : {
            kind: 'dayCategory',
            sourceCategoryLabelKey,
            targetCategoryLabelKey,
          }
    }
    case SimpleForecastSourceDateMismatch.NONE:
    case SimpleForecastSourceDateMismatch.UNSPECIFIED:
    default:
      return undefined
  }
}
